import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Lightbulb, Send } from "lucide-react";

const CATEGORIES = [
  { value: "feature", label: "Feature idea" },
  { value: "feedback", label: "Feedback" },
  { value: "bug", label: "Bug report" },
  { value: "other", label: "Other" },
];

/**
 * Private suggestion box for members.
 * Submissions go to admins only — attributed (admins see who sent it).
 */
const SuggestionBox = () => {
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("feature");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      await axios.post(`${API}/suggestions`, { content: trimmed, category });
      toast.success("Thanks — your suggestion was sent privately to the admin team.");
      setContent("");
      setCategory("feature");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send suggestion");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] text-white p-6 md:p-8" data-testid="suggestion-box">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-[#5170FF] flex items-center justify-center flex-shrink-0">
          <Lightbulb className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-display font-bold text-xl text-[#5170FF]">Suggestion box</h3>
          <p className="text-sm text-white/70">
            Have an idea, request, or piece of feedback? Drop it here — only admins see it.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => setCategory(c.value)}
              data-testid={`suggestion-cat-${c.value}`}
              className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border-2 transition-colors ${
                category === c.value
                  ? "bg-[#FF2D87] text-white border-[#FF2D87]"
                  : "bg-transparent text-white/70 border-white/30 hover:border-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Tell us what's on your mind…"
          rows={4}
          maxLength={2000}
          required
          className="w-full bg-white text-[#0A0A0A] p-3 border-2 border-white focus:border-[#FF2D87] focus:outline-none resize-y"
          data-testid="suggestion-content-input"
        />

        <div className="flex items-center justify-between">
          <span className="text-xs text-white/50">{content.length}/2000 · sent privately to admins</span>
          <button
            type="submit"
            disabled={sending || !content.trim()}
            className="bg-[#FF2D87] text-white font-bold rounded-full px-5 py-2.5 hover:bg-white hover:text-[#0A0A0A] transition-colors text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="suggestion-submit"
          >
            <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send suggestion"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SuggestionBox;
